/* eslint-disable @typescript-eslint/no-explicit-any */
// Dashboard Templates (System Views)
//
// Note: These are called "Templates" in the code (technical term) but
// are presented to users as "System Views" in the UI. They define
// pre-configured dashboard layouts that users can select and customize.
//
// Extracted from widgetRegistry.ts to reduce file size.

import { PERMISSIONS } from '../../utils/permissions';
import { REGISTRY_WIDGET_SIZES } from './widgets/widgetRegistryConstants';

const WIDGET_SIZES = REGISTRY_WIDGET_SIZES;

// GenAIOps Dashboard Templates
export const GENAIOPS_TEMPLATES = {
  overview: {
    id: 'overview',
    name: 'GenAI Overview',
    description: 'Balanced view of all LLM metrics',
    isDefault: true,
    widgets: [
      { i: 'total-traces', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-llm-cost', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'average-latency', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-error-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-sessions', x: 0, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-tokens', x: 1, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-quality', x: 2, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-prompts', x: 3, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'traces-over-time', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM },
      { i: 'model-usage-distribution', x: 2, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  performance: {
    id: 'performance',
    name: 'GenAI Performance',
    description: 'Latency and error metrics focus',
    isDefault: false,
    widgets: [
      { i: 'average-latency', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-p95-latency', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-p99-latency', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-error-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-traces', x: 0, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'total-sessions', x: 1, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'traces-over-time', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  cost: {
    id: 'cost',
    name: 'GenAI Cost Analysis',
    description: 'Cost breakdown and token usage',
    isDefault: false,
    widgets: [
      { i: 'total-llm-cost', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-tokens', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-traces', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-sessions', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'model-usage-distribution', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'traces-over-time', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  quality: {
    id: 'quality',
    name: 'GenAI Quality',
    description: 'Quality and feedback metrics',
    isDefault: false,
    widgets: [
      { i: 'genaiops-quality', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-error-rate', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-traces', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'genaiops-prompts', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'traces-over-time', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'model-usage-distribution', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// Hub Dashboard Templates
export const HUB_TEMPLATES = {
  executive: {
    id: 'executive',
    name: 'Executive',
    description: 'Project management: budget, hours, project status',
    isDefault: true,
    widgets: [
      // Row 0: Project KPIs (4 small widgets)
      { i: 'active-projects', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-budget', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'burn-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      // Row 1-2: Charts (2x2 each, side by side)
      { i: 'project-status-distribution', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'recent-projects', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM },
      // Row 3-4: More charts
      { i: 'monthly-spend-trend', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'hours-by-person', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM },
    ]
  },
  technical: {
    id: 'technical',
    name: 'Command Center',
    description: 'Platform health and cross-module alerts',
    isDefault: false,
    widgets: [
      // Row 0: Platform KPIs (4 small)
      { i: 'system-health-score', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'unified-total-cost', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-active-operations', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'platform-error-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      // Row 1: Module Status (full width)
      { i: 'module-status-grid', x: 0, y: 1, ...WIDGET_SIZES.WIDE },
      // Row 2: Module Metrics (4 small)
      { i: 'agent-activity-summary', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'pipeline-health', x: 1, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'cloud-compliance-summary', x: 2, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'active-projects', x: 3, y: 2, ...WIDGET_SIZES.SMALL },
      // Row 3-4: Charts and Alerts
      { i: 'llm-cost-trend', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'cross-platform-alerts', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM },
    ]
  },
  aws_insights: {
    id: 'aws_insights',
    name: 'AWS Insights',
    description: 'Funding pipeline and project health for AWS Partners',
    isDefault: false,
    requiredPermissions: [
      PERMISSIONS.AWS_ACCESS_VIEW,
      PERMISSIONS.AWS_ACCESS_MANAGE,
      PERMISSIONS.PARTNERS_VIEW,
      PERMISSIONS.PARTNERS_MANAGE
    ],
    widgets: [
      // Row 1: Key Metrics (4 columns)
      { i: 'funding-active-requests', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'funding-total-credits', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'funding-credits-expiring', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'funding-approval-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },

      // Row 2: Infrastructure Metrics (2 columns)
      { i: 'total-infrastructure-spend-combined', x: 0, y: 1, w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 4 },
      { i: 'infrastructure-budget-utilization', x: 2, y: 1, w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 4 },

      // Row 3: Funding Charts (2 columns)
      { i: 'funding-by-program', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM },
      { i: 'funding-requests-timeline', x: 2, y: 2, ...WIDGET_SIZES.MEDIUM },

      // Row 4: Credits Charts (2 columns)
      { i: 'credits-by-client', x: 0, y: 4, ...WIDGET_SIZES.MEDIUM },
      { i: 'credit-expiration-timeline', x: 2, y: 4, ...WIDGET_SIZES.MEDIUM },

      // Row 5: Activity Lists (2 columns)
      { i: 'funding-recent-requests', x: 0, y: 6, ...WIDGET_SIZES.MEDIUM },
      { i: 'projects-needing-attention', x: 2, y: 6, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// Health Dashboard Templates
// Cross-client health monitoring for AWS and Partner users
export const HEALTH_TEMPLATES = {
  default: {
    id: 'health-default',
    name: 'Health Overview',
    description: 'Project delivery, budgets, and support health',
    isDefault: true,
    widgets: [
      // Row 0: Key Delivery Metrics (4 small cards)
      { i: 'on-time-delivery-rate', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'projects-behind-schedule', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'budget-burn-rate', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'pending-support-requests', x: 3, y: 0, ...WIDGET_SIZES.SMALL },

      // Row 1-2: Main Visualizations (2 medium cards side by side)
      { i: 'project-health-heatmap', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'infrastructure-spend-by-client', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM },

      // Row 3-4: Lists side by side (2 medium cards)
      { i: 'critical-projects-list', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'budget-alerts-list', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM },

      // Row 5: 4 small metric cards across
      { i: 'milestone-completion-rate', x: 0, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'avg-project-delay', x: 1, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'budget-overrun-alert', x: 2, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'total-active-projects', x: 3, y: 5, ...WIDGET_SIZES.SMALL },

      // Row 6-7: Full-width support requests list (4 columns wide, 2 rows tall)
      { i: 'open-support-requests-list', x: 0, y: 6, w: 4, h: 2, minW: 2, minH: 2, maxW: 4, maxH: 4 },

      // Row 8-9: Funding & Credits Visualizations (2 medium cards side by side)
      { i: 'funding-by-program-health', x: 0, y: 8, ...WIDGET_SIZES.MEDIUM },
      { i: 'credits-by-client-health', x: 2, y: 8, ...WIDGET_SIZES.MEDIUM },

      // Row 10-11: Credit Expiration Timeline (medium on left)
      { i: 'credit-expiration-health', x: 0, y: 10, ...WIDGET_SIZES.MEDIUM },

      // Row 10-11: Recent Funding Requests (medium on right)
      { i: 'funding-requests-health', x: 2, y: 10, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// DataOps Dashboard Templates
export const DATAOPS_TEMPLATES = {
  overview: {
    id: 'overview',
    name: 'Overview',
    description: 'Comprehensive data operations view',
    isDefault: true,
    widgets: [
      // Row 0: Key metrics (4 SMALL cards)
      { i: 'total-datasets', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-jobs', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'pipeline-success-rate', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'events-today', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      // Row 1: Run statistics (4 SMALL cards)
      { i: 'successful-runs', x: 0, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'failed-runs', x: 1, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'running-jobs', x: 2, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'degraded-datasets', x: 3, y: 1, ...WIDGET_SIZES.SMALL },
      // Row 2: Charts (2 MEDIUM)
      { i: 'pipeline-runs-chart', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM },
      { i: 'runs-by-status', x: 2, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  quality: {
    id: 'quality',
    name: 'Data Quality',
    description: 'Focus on data quality metrics',
    isDefault: false,
    widgets: [
      { i: 'data-quality-score', x: 0, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'total-datasets', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'successful-runs', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'failed-runs', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'pipeline-runs-chart', x: 1, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  pipelines: {
    id: 'pipelines',
    name: 'Pipeline Runs',
    description: 'Focus on job execution status',
    isDefault: false,
    widgets: [
      { i: 'successful-runs', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'failed-runs', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-datasets', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'data-quality-score', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'pipeline-runs-chart', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// CloudOps (PlatformOps) Dashboard Templates
export const CLOUDOPS_TEMPLATES = {
  overview: {
    id: 'overview',
    name: 'Overview',
    description: 'Comprehensive cloud operations view',
    isDefault: true,
    widgets: [
      // Row 0: Key business metrics (4 SMALL cards)
      { i: 'platform-health', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-cloud-spend', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'compliance-score', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'estimated-savings', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      // Row 1: Environment health status (4 SMALL cards)
      { i: 'healthy-environments', x: 0, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'degraded-environments', x: 1, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'active-environments', x: 2, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'open-violations', x: 3, y: 1, ...WIDGET_SIZES.SMALL },
      // Row 2: Charts (2 MEDIUM)
      { i: 'cost-by-service', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM },
      { i: 'burn-rate-vs-planned', x: 2, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  cost: {
    id: 'cost',
    name: 'Cost Analysis',
    description: 'Focus on cloud spend and savings',
    isDefault: false,
    widgets: [
      { i: 'total-cloud-spend', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'estimated-savings', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'cost-by-service', x: 2, y: 0, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  compliance: {
    id: 'compliance',
    name: 'Compliance',
    description: 'Focus on violations and compliance',
    isDefault: false,
    widgets: [
      { i: 'compliance-score', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'open-violations', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'active-environments', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'platform-health', x: 3, y: 0, ...WIDGET_SIZES.SMALL }
    ]
  }
};

// AgentOps Dashboard Templates
export const AGENTOPS_TEMPLATES = {
  overview: {
    id: 'overview',
    name: 'Agent Overview',
    description: 'Comprehensive agent metrics view',
    isDefault: true,
    widgets: [
      { i: 'agentops-active-runs', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agent-success-rate', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-total-tools', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-safety-violations', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-health-score', x: 0, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-heap-usage', x: 1, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-cpu-usage', x: 2, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'safety-score', x: 3, y: 1, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-runs-timeline', x: 0, y: 2, ...WIDGET_SIZES.MEDIUM },
      { i: 'agentops-tool-success', x: 2, y: 2, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  operations: {
    id: 'operations',
    name: 'Agent Operations',
    description: 'Agent runs and tool usage focus',
    isDefault: false,
    widgets: [
      { i: 'agentops-active-runs', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agent-runs-count', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agent-success-rate', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-total-tools', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-runs-timeline', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'tool-usage', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'agentops-top-agents', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  safety: {
    id: 'safety',
    name: 'Agent Safety',
    description: 'Safety violations and compliance',
    isDefault: false,
    widgets: [
      { i: 'agentops-safety-violations', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'safety-score', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agent-success-rate', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-active-runs', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-incidents', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'agentops-tool-success', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  performance: {
    id: 'performance',
    name: 'Agent Performance',
    description: 'APM and system health metrics',
    isDefault: false,
    widgets: [
      { i: 'agentops-health-score', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-heap-usage', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-cpu-usage', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agent-success-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'agentops-runs-timeline', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'agentops-tool-success', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// AI Operations Combined Templates (GenAI + Agent)
export const AI_OPERATIONS_TEMPLATES = {
  ...GENAIOPS_TEMPLATES,
  ...Object.fromEntries(
    Object.entries(AGENTOPS_TEMPLATES).map(([key, val]) => [`agent_${key}`, val])
  ),
};

// Helper to get default layout for a dashboard context
export const getDefaultLayoutForContext = (context: string) => {
  switch (context) {
    case 'genaiops':
    case 'ai-operations':
      return GENAIOPS_TEMPLATES.overview.widgets;
    case 'agentops':
      return AGENTOPS_TEMPLATES.overview.widgets;
    case 'dataops':
      return DATAOPS_TEMPLATES.overview.widgets;
    case 'cloudops':
      return CLOUDOPS_TEMPLATES.overview.widgets;
    case 'health':
      return HEALTH_TEMPLATES.default.widgets;
    case 'hub':
      return HUB_TEMPLATES.executive.widgets;
    default:
      return HUB_TEMPLATES.executive.widgets;
  }
};

// Helper to get templates (system views) for a dashboard context
// Returns the template definitions which are displayed as "System Views" in the UI
// Filters templates based on permissions if requiredPermissions is specified
const getUserPermissionNames = (user: unknown): string[] => {
  const u = user as Record<string, unknown> | null;
  if (!u?.permissions || !Array.isArray(u.permissions)) {
    return [];
  }
  return (u.permissions as unknown[])
    .map((permission: unknown) => (typeof permission === 'string' ? permission : (permission as Record<string, unknown>)?.name as string))
    .filter(Boolean);
};

export const getTemplatesForContext = (context: string, user: unknown = null) => {
  let templates;

  switch (context) {
    case 'genaiops':
      templates = GENAIOPS_TEMPLATES;
      break;
    case 'agentops':
      templates = AGENTOPS_TEMPLATES;
      break;
    case 'ai-operations':
      templates = AI_OPERATIONS_TEMPLATES;
      break;
    case 'dataops':
      templates = DATAOPS_TEMPLATES;
      break;
    case 'cloudops':
      templates = CLOUDOPS_TEMPLATES;
      break;
    case 'health':
      templates = HEALTH_TEMPLATES;
      break;
    case 'hub':
      templates = HUB_TEMPLATES;
      break;
    default:
      templates = HUB_TEMPLATES;
  }

  // If no user provided, return all templates (backwards compatibility)
  if (!user) {
    return templates;
  }

  const userPermissions = getUserPermissionNames(user);
  const isAwsUser =
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_VIEW) ||
    userPermissions.includes(PERMISSIONS.AWS_ACCESS_MANAGE) ||
    Boolean((user as Record<string, unknown>)?.aws_job_title) ||
    Array.isArray((user as Record<string, unknown>)?.aws_accessible_modules);


  // Filter templates by requiredPermissions and adjust defaults based on context
  const filteredTemplates: Record<string, unknown> = {};
  Object.entries(templates).forEach(([key, template]) => {
    let shouldInclude = false;
    const tmpl = template as Record<string, unknown>;

    // If template has requiredPermissions, check if user has any
    if (tmpl.requiredPermissions && Array.isArray(tmpl.requiredPermissions)) {
      shouldInclude = (tmpl.requiredPermissions as string[]).some((permission: string) =>
        userPermissions.includes(permission)
      );
    } else {
      // No role requirement = available to all
      shouldInclude = true;
    }

    if (shouldInclude) {
      // Clone the template to avoid mutating the original
      const templateCopy = { ...template };

      // Special handling for AWS Insights template default status
      if (key === 'aws_insights') {
        // AWS users: AWS Insights is default
        // Others: Executive remains default
        templateCopy.isDefault = isAwsUser;
      } else if (key === 'executive') {
        // For Executive template, make it NOT default for AWS users if they have AWS Insights
        if (isAwsUser) {
          templateCopy.isDefault = false;
        }
      }

      filteredTemplates[key] = templateCopy;
    }
  });

  return filteredTemplates;
};

// Backwards compatibility alias
export const getPresetsForContext = getTemplatesForContext;
